import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { ABOUT } from "@/data/content";
import { Compass, Target, Star } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/api";

export default function About() {
  const { settings } = useSettings();
  const pageData = settings?.about_page || {};

  const title = pageData.title || "Menghubungkan harapan menjadi dampak lintas generasi";
  const summary = pageData.executiveSummary || ABOUT.executiveSummary;
  const quote = pageData.catalystStatement || ABOUT.catalystStatement;
  const vision = pageData.vision || ABOUT.vision;
  const mission = Array.isArray(pageData.mission) && pageData.mission.length > 0 ? pageData.mission : ABOUT.mission;
  const history = Array.isArray(pageData.history) && pageData.history.length > 0 ? pageData.history : ABOUT.history;
  const philosophy = Array.isArray(pageData.philosophy) && pageData.philosophy.length > 0 ? pageData.philosophy : ABOUT.philosophy;
  const values = Array.isArray(pageData.values) && pageData.values.length > 0 ? pageData.values : ABOUT.values;

  return (
    <div data-testid="page-about">
      <SeoHead
        title={{ id: "Tentang Yayasan", en: "About the Foundation" }}
        description={{ id: summary, en: "Learn about Sauyunan Foundation's history, vision, mission, philosophy, and values rooted in Sundanese wisdom." }}
      />
      <PageHeader
        eyebrow="Tentang Kami"
        title={title}
        description={summary}
        image={getImageUrl("https://images.unsplash.com/photo-1611087802810-046bb671e644?w=1200&q=80")}
      />

      {/* Executive summary continuation */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-sm">
          <p className="text-lg md:text-xl leading-relaxed text-slate-700 font-serif-display italic">
            "{quote}"
          </p>
        </div>
      </section>

      {/* History / Story */}
      <section id="sejarah" className="section-y bg-[var(--bg)]">
        <div className="container-sf">
          <div className="grid lg:grid-cols-[1fr,2fr] gap-12">
            <div className="lg:sticky lg:top-28 lg:self-start">
              <div className="eyebrow mb-3">Sejarah & Latar Belakang</div>
              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                Berakar pada{" "}
                <span className="font-serif-display italic font-normal text-[var(--brand-blue)]">kearifan lokal</span>,
                menjangkau Nusantara.
              </h2>
            </div>
            <div className="space-y-8">
              {history.map((h, i) => (
                <div key={i} className="flex gap-6 group" data-testid={`history-${i}`}>
                  <div className="shrink-0">
                    <div className="marker-num text-5xl md:text-6xl leading-none">0{i + 1}</div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900">{h.title}</h3>
                    <p className="mt-2 text-slate-600 leading-relaxed whitespace-pre-wrap">{h.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section id="visi-misi" className="section-y bg-white">
        <div className="container-sf">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="card-sf p-10 relative overflow-hidden" data-testid="about-vision">
              <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-[var(--brand-blue)]/8" />
              <Compass className="h-10 w-10 text-[var(--brand-blue)] mb-6" />
              <div className="eyebrow text-[var(--brand-blue)] mb-2">Visi</div>
              <h3 className="text-3xl font-bold text-slate-900 mb-5">Visi Kami</h3>
              <p className="text-slate-700 leading-relaxed text-lg whitespace-pre-wrap">{vision}</p>
            </div>
            <div className="card-sf p-10 relative overflow-hidden" data-testid="about-mission">
              <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-[var(--brand-orange)]/10" />
              <Target className="h-10 w-10 text-[var(--brand-orange)] mb-6" />
              <div className="eyebrow text-[var(--brand-orange)] mb-2">Misi</div>
              <h3 className="text-3xl font-bold text-slate-900 mb-5">Tujuh Misi Utama</h3>
              <ol className="space-y-3">
                {mission.map((m, i) => (
                  <li key={i} className="flex gap-3 text-slate-700 text-sm leading-relaxed">
                    <span className="font-bold text-[var(--brand-orange)] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section id="filosofi" className="section-y bg-[#0B1A3D] text-white relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="container-sf relative">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow text-[var(--brand-orange)] mb-4">Filosofi Sunda</div>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              Empat pilar yang membentuk cara kami bekerja
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {philosophy.map((p, i) => (
              <div key={p.key || i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-7" data-testid={`about-philosophy-${(p.key || '').toLowerCase().replace(' ','-')}`}>
                <div className="text-5xl font-serif-display italic text-[var(--brand-orange)]/40 mb-3">0{i + 1}</div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-orange)] mb-1">{p.subtitle}</div>
                <h3 className="text-2xl font-bold mb-3">{p.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section id="nilai" className="section-y bg-[var(--bg)]">
        <div className="container-sf">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow mb-3">Nilai-nilai Organisasi</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
              Tujuh nilai yang{" "}
              <span className="font-serif-display italic font-normal text-[var(--brand-blue)]">kami pegang</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <div key={v.key || i} className="card-sf p-7" data-testid={`about-value-${v.key}`}>
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-4 ${i % 2 === 0 ? "bg-[var(--brand-blue)]" : "bg-[var(--brand-orange)]"} text-white`}>
                  <Star className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organizational Positioning */}
      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow mb-3">Positioning Organisasi</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
              Empat peran strategis dalam ekosistem dampak
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {ABOUT.positioning.map((p, i) => (
              <div key={i} className="card-sf p-7 flex gap-5" data-testid={`positioning-${i}`}>
                <div className="marker-num text-5xl leading-none shrink-0">0{i + 1}</div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aspiration 2045 */}
      <section className="section-y bg-gradient-to-br from-[var(--brand-blue)] to-[#254DAA] text-white relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[var(--brand-orange)]/25 blur-3xl" />
        <div className="container-sf relative">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow text-[var(--brand-orange)] mb-3">Aspirasi 2045</div>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              Tujuh pilar cita-cita Indonesia Emas 2045
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ABOUT.aspiration2045.map((a, i) => (
              <div key={i} className="rounded-2xl border border-white/15 bg-white/5 p-6 backdrop-blur">
                <div className="marker-num text-3xl mb-3" style={{ background: "linear-gradient(135deg, #fff 0%, #F28C1F 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>0{i + 1}</div>
                <div className="text-sm md:text-base font-semibold leading-tight">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
